import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Signal,
  ViewChild,
  WritableSignal,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { ICONS } from '@gfe/core/services/icons/icons.service';
import { isNil } from '@gfe/core/utils/shared-utils';

import { foldForSearch } from '@core/utils/search-text.utils';

import {
  ButtonSelectComponent,
  ButtonSelectValue
} from '@shared/components/button-select/button-select.component';
import { ButtonComponent, ButtonType } from '@shared/components/button/button.component';
import { DataspaceSelectorComponent } from '@shared/components/dataspace-selector/dataspace-selector.component';
import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { ItemsDropdownComponent } from '@shared/components/dropdown/template/items-dropdown/items-dropdown.component';
import { EntityAutoCompleteComponent } from '@shared/components/row-edition-form/entity-auto-complete/entity-auto-complete.component';
import {
  QualifiersConfig,
  SearchQueryBuilderComponent,
  SearchResult
} from '@shared/components/search-query-builder/search-query-builder.component';
import { SelectItem } from '@shared/components/select/select-item.model';
import { DropdownItem, DropdownSearchConfig } from '@shared/models/dropdown-item.model';
import { isEditableTarget } from '@shared/utils/editable-target.utils';

import { PaginationComponent, PaginationConfig } from '../pagination/pagination.component';
import { TooltipComponent } from '../tooltip/tooltip.component';

export interface ToolboxButton {
  id: string;
  icon?: string;
  tooltip?: string;
  label?: string;
  /** What assistive tech names the button by, when its tooltip says something else. */
  ariaLabel?: string;
  callToAction?: boolean;
  callback: () => void;
  active?: boolean;
  disabled?: boolean;
  /** Draws an unobtrusive dot on the button, for state the user should act on. */
  indicator?: boolean;
}

export interface ToolboxDropdownButton {
  id: string;
  label?: string;
  icon?: string;
  tooltip: string;
  /** What assistive tech names the button by, when its tooltip says something else. */
  ariaLabel?: string;
  searchConfig?: DropdownSearchConfig;
  callToAction?: boolean;
  items: DropdownItem[];
  disabled?: boolean;
}

/**
 * A toolbar action whose value is a reference to another entity. Carries the entity type
 * rather than a list of options: the picker searches the backend, so the toolbar never has
 * to hold a whole entity table in memory to offer one.
 */
export interface ToolboxEntityRefButton {
  kind: 'entity-ref';
  id: string;
  icon?: string;
  label?: string;
  tooltip?: string;
  disabled?: boolean;
  entityType: string;
  onPick: (uid: string) => void;
  /** Offers a "no value" entry, for an assignment that has to be undoable in bulk. */
  onClear?: () => void;
}

export interface ToolboxSelectDropdownButton {
  kind: 'select-dropdown';
  id: string;
  items: SelectItem[];
  value: ButtonSelectValue;
  onValueChange: (value: ButtonSelectValue) => void;
  onSearchChange?: (term: string) => void;
  /** Defaults to whether `onSearchChange` is set: the dropdown never filters on its own. */
  enableSearch?: boolean;
  tooltip?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  triggerIconClass?: string | null;
  disabled?: boolean;
}

export interface ToolboxDataspaceSelectorButton {
  kind: 'dataspace-selector';
  id: string;
  value: number | null;
  onValueChange: (id: number | null) => void;
  tooltip?: string;
  placeholder?: string;
  disabled?: boolean;
}

export type ToolboxItem =
  | ToolboxButton
  | ToolboxDropdownButton
  | ToolboxEntityRefButton
  | ToolboxSelectDropdownButton
  | ToolboxDataspaceSelectorButton;

export interface ToolboxSection {
  id: string;
  column?: boolean;
  items: ToolboxItem[];
}

export type ToolboxOrientation = 'horizontal' | 'vertical';

export interface SearchConfig {
  active: boolean;
  shortcut?: boolean;
  type?: 'simple' | 'advanced';
  qualifiers?: QualifiersConfig;
}

@Component({
  selector: 'app-toolbox',
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    PaginationComponent,
    DropdownComponent,
    ItemsDropdownComponent,
    SearchQueryBuilderComponent,
    TooltipComponent,
    DataspaceSelectorComponent,
    ButtonSelectComponent,
    ButtonComponent,
    EntityAutoCompleteComponent
  ],
  templateUrl: './toolbox.component.html'
})
export class ToolboxComponent {
  sections = input<ToolboxSection[]>([]);
  orientation = input<ToolboxOrientation>('horizontal');
  searchConfig = input<SearchConfig>({
    active: false
  });
  useAdvancedFiltering = input<boolean>(false);
  openFilterForField = input<string | null>(null);
  showPageSelector = input<boolean>(false);
  showSelect = input<boolean>(false);
  showCustomContent = input<boolean>(false);
  shouldFocus = input<boolean>(false);
  selectedItemsCount = input<number>(0);
  displayedItemCount = input<number>(0);
  totalItems = input<number>(0);
  showPlanItems = input<boolean>(false);
  searchPlaceholder = input<string>('Search...');
  paginationConfig = input<PaginationConfig>({
    currentPage: 1,
    totalPages: 1
  });
  selectionVariant = input<'data' | 'legacy'>('legacy');

  searchQuery = model<string>('');
  showAdvancedFiltering = model<boolean>(false);

  // Dropdown-specific search state
  protected dropdownSearchTerms = new Map<string, WritableSignal<string>>();

  searchTermChange = output<string>();
  dropdownSearchChange = output<{ dropdownId: string; term: string }>();
  advancedQuerySearch = output<SearchResult>();
  pageChange = output<number>();
  selectOnly = output<boolean>();
  deselectAll = output<void>();
  loadAllRows = output<void>();
  addToPlan = output<void>();

  protected isSearchFocused = signal(false);
  protected isSelectionDropdownOpen = signal<boolean>(false);
  protected dropdownOpenStates = new Map<string, WritableSignal<boolean>>();
  private selectOnlyActive = signal<boolean>(false);
  private translate = inject(TranslateService);

  protected loadAllRowsDisabled = computed(() => this.totalItems() > 500);

  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  @ViewChild('filterInput') inputRef!: ElementRef<HTMLInputElement>;
  @ViewChild(SearchQueryBuilderComponent) searchQueryBuilder!: SearchQueryBuilderComponent;

  constructor() {
    effect(() => {
      if (this.selectedItemsCount() === 0) {
        this.selectOnlyActive.set(false);
      }
    });
    effect(() => {
      if (this.shouldFocus()) {
        queueMicrotask(() => {
          if (!isNil(this.inputRef?.nativeElement)) {
            this.inputRef.nativeElement.focus();
          }
        });
      }
    });

    // Auto-enable advanced filtering when a filter is added from column header
    effect(() => {
      const fieldToOpen = this.openFilterForField();
      if (!isNil(fieldToOpen) && this.useAdvancedFiltering()) {
        this.showAdvancedFiltering.set(true);
      }
    });
  }

  updateSearchQuery(newQuery: string): void {
    this.searchQueryBuilder.queryValue.set(newQuery);
  }

  onButtonClick(button: ToolboxButton): void {
    if (!button.disabled) {
      button.callback();
    }
  }

  isSelectDropdownButton(item: ToolboxItem): item is ToolboxSelectDropdownButton {
    return 'kind' in item && item.kind === 'select-dropdown';
  }

  isDataspaceSelectorButton(item: ToolboxItem): item is ToolboxDataspaceSelectorButton {
    return 'kind' in item && item.kind === 'dataspace-selector';
  }

  isEntityRefButton(item: ToolboxItem): item is ToolboxEntityRefButton {
    return 'kind' in item && item.kind === 'entity-ref';
  }

  /**
   * The picker is generic over single and array references, so it reports the wider type.
   * A toolbar action writes one value; an empty one arrives through `onEntityRefClear`.
   */
  onEntityRefPick(item: ToolboxEntityRefButton, value: string | string[] | null): void {
    if (typeof value !== 'string' || value.length === 0) return;
    item.onPick(value);
    // A toolbar action is done once a value is chosen; leaving the panel open would invite
    // a second assignment nobody asked for.
    this.closeDropdown(item.id)();
  }

  onEntityRefClear(item: ToolboxEntityRefButton): void {
    if (isNil(item.onClear)) return;
    item.onClear();
    this.closeDropdown(item.id)();
  }

  isDropdownButton(item: ToolboxItem): item is ToolboxDropdownButton {
    return (
      'items' in item &&
      !this.isSelectDropdownButton(item) &&
      !this.isDataspaceSelectorButton(item)
    );
  }

  isRegularButton(item: ToolboxItem): item is ToolboxButton {
    return 'callback' in item;
  }

  onSelectOnly(): void {
    if (this.showSelect()) {
      const newState = !this.selectOnlyActive();
      this.selectOnlyActive.set(newState);
      this.selectOnly.emit(newState);
    }
  }

  onDropdownSearchTermChange(term: string, dropdownId: string): void {
    const signal = this.getDropdownSearchSignal(dropdownId);
    signal.set(term);
    this.dropdownSearchChange.emit({ dropdownId, term });
  }

  onMainToolboxSearchChange(newQuery: string): void {
    const currentTerm = this.searchQuery();
    if (newQuery !== currentTerm) {
      this.searchQuery.set(newQuery);
      this.searchTermChange.emit(this.searchQuery());
    }
  }

  getDropdownSearchSignal(dropdownId: string): WritableSignal<string> {
    if (!this.dropdownSearchTerms.has(dropdownId)) {
      this.dropdownSearchTerms.set(dropdownId, signal<string>(''));
    }
    return this.dropdownSearchTerms.get(dropdownId)!;
  }

  getDropdownSearchTerm(dropdownId: string): Signal<string> {
    return this.getDropdownSearchSignal(dropdownId).asReadonly();
  }

  getFilteredDropdownItems(dropdownId: string, items: DropdownItem[]): DropdownItem[] {
    const searchTerm = this.getDropdownSearchSignal(dropdownId)();
    if (!searchTerm || searchTerm.trim().length === 0) {
      return items;
    }

    const foldedTerm = foldForSearch(searchTerm);
    return items.filter((item) => {
      // Always show separators
      if ('isSeparator' in item && item.isSeparator) return true;
      // Filter fulfilled items by label
      if ('label' in item) {
        return foldForSearch(item.label).includes(foldedTerm);
      }
      return false;
    });
  }

  onLoadAllRows(): void {
    if (this.loadAllRowsDisabled()) return;
    this.loadAllRows.emit();
    this.isSelectionDropdownOpen.set(false);
  }

  onDeselectAll(): void {
    this.selectOnlyActive.set(false);
    this.deselectAll.emit();
    this.isSelectionDropdownOpen.set(false);
  }

  getSelectionDropdownItems(): DropdownItem[] {
    this.currentLang();
    const showSelectedItem: DropdownItem = {
      label: this.translate.instant('features.data.toolbox.select_only'),
      action: () => this.onSelectOnly(),
      disabled: false,
      iconClass: ICONS.listBullets,
      isSelected: this.selectOnlyActive()
    };
    const clearItem: DropdownItem = {
      label: this.translate.instant('common.clear'),
      action: () => this.onDeselectAll(),
      disabled: this.selectedItemsCount() === 0,
      iconClass: ICONS.x
    };

    if (this.selectionVariant() === 'data') {
      return [
        {
          label: this.translate.instant('features.data.toolbox.load_all_rows'),
          action: () => this.onLoadAllRows(),
          disabled: this.loadAllRowsDisabled(),
          iconClass: ICONS.rows
        },
        showSelectedItem,
        clearItem
      ];
    }

    return [showSelectedItem, clearItem];
  }

  // Selection dropdown methods
  toggleSelectionDropdown(): void {
    this.isSelectionDropdownOpen.update((v) => !v);
  }

  onCloseSelectionDropdown = (): void => {
    this.isSelectionDropdownOpen.set(false);
  };

  // Dynamic dropdown methods
  getDropdownOpenState(id: string): WritableSignal<boolean> {
    if (!this.dropdownOpenStates.has(id)) {
      this.dropdownOpenStates.set(id, signal<boolean>(false));
    }
    return this.dropdownOpenStates.get(id)!;
  }

  toggleDropdown(id: string, disabled?: boolean): void {
    if (disabled) return;
    const state = this.getDropdownOpenState(id);
    state.update((v) => !v);
  }

  closeDropdown(id: string): () => void {
    return () => {
      const state = this.getDropdownOpenState(id);
      state.set(false);

      // Clear dropdown search term when closing and notify parent
      const searchSignal = this.dropdownSearchTerms.get(id);
      if (searchSignal) {
        searchSignal.set('');
        this.dropdownSearchChange.emit({ dropdownId: id, term: '' });
      }
    };
  }

  getSelectionLabel(): string {
    return this.translate.instant('features.data.toolbox.select', {
      count: this.selectedItemsCount(),
      total: this.totalItems()
    });
  }

  onPageChange(newPage: number): void {
    this.pageChange.emit(newPage);
  }

  onExportClick(): void {
    this.addToPlan.emit();
  }

  getItemClasses(item: ToolboxButton): string {
    const baseClasses: string =
      'hover:bg-interactive flex items-center justify-center rounded transition-colors';
    let result = '';

    if (item.disabled) {
      result += `${baseClasses} cursor-not-allowed opacity-50 hover:bg-none`;
    } else {
      result = `${baseClasses} cursor-pointer opacity-100`;
    }

    if (item.callToAction) {
      result += ' btn btn-primary w-full px-4';
    } else {
      if (!item.disabled && !item.active) {
        result += ' hover:bg-interactive hover:text-text-primary';
      }

      if (!item.active) {
        result += ' text-secondary';
      }
      result += ' h-7 w-7';
    }

    if (item.active) {
      result += ' bg-accent-main group-hover:bg-accent-main text-white';
    }

    return result;
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Escape':
        if (this.isSearchFocused()) {
          this.inputRef.nativeElement.blur();
        }
        break;
      case '/':
        if (
          this.searchConfig().type !== 'advanced' &&
          this.searchConfig().active &&
          this.searchConfig().shortcut &&
          !isEditableTarget(event.target)
        ) {
          event.preventDefault();
          this.inputRef.nativeElement.focus();
        }
        break;
    }
  }

  protected toggleAdvancedFiltering() {
    this.showAdvancedFiltering.update((v) => !v);
  }

  protected readonly ICONS = ICONS;

  protected getButtonType(item: ToolboxButton): ButtonType {
    if (item.callToAction) {
      return 'primary';
    }
    if (isNil(item.label) && !isNil(item.icon)) {
      return 'icon';
    }

    return 'transparent';
  }
}
